import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OnboardingCarousel from './features/auth/components/OnboardingCarousel';
import Tasks from './features/tasks/pages/Tasks';
import LuckySpinPage from './features/spin/pages/LuckySpinPage';
import Inventory from './features/inventory/pages/Inventory';
import TicketCollectorModal from './pages/TicketCollectorModal';
import DailyRewardPage from './features/rewards/pages/DaillyRewardPage';
import TonStorePage from './features/shop/pages/Shop';
import FriendsLeaderboardPage from './features/social/pages/FriendsLeaderboardPage';
import EarlyAccessPage from './features/shop/pages/EarlyAccessPage';
import BattlePassPage from './features/battlePass/pages/BattlePassPage';
import TonWithdrawalPage from './features/wallet/pages/TonWithdrawalPage';
import AppLayout from './shared/components/AppLayout';
import GameShell from './shared/components/GameShell';
import LairPage from './features/lair/pages/LairPage';
import ArenaPage from './features/minigames/brawl/pages/ArenaPage';
import DurakPage from './features/minigames/durak/pages/DurakPage';
import BrawlerPage from './features/minigames/brawl/pages/BrawlerPage';
import Match3Page from './features/minigames/match3/Match3Page';
import SolitairePage from './features/minigames/solitaire/SolitairePage';
import { inviteRoute, takeLaunchInvite } from './features/minigames/common/pvpInvite';

/**
 * Компонент для определения маршрутов приложения
 *
 * Управляет условной логикой рендеринга:
 * - Онбординг для новых пользователей
 * - Ежедневная награда при необходимости
 * - Основные маршруты приложения
 */
export const AppRoutes = ({ balanceRef, onRewardComplete }) => {
  const user = useSelector(state => state.user.data);
  const showDailyReward = useSelector(state => state.ui.modals.dailyReward);
  const navigate = useNavigate();
  const mainRoutes = user.onboarding !== false && !showDailyReward;

  // Opened from a PvP invite link (startapp=<id>_arena|durak_<CODE>) → straight to that game's lobby,
  // once the onboarding / daily reward are out of the way
  useEffect(() => {
    if (!mainRoutes) return;
    const invite = takeLaunchInvite();
    if (invite) navigate(inviteRoute(invite), { replace: true });
  }, [mainRoutes, navigate]);

  console.log("REFRESH APPROUTES")
  // Если пользователь не прошел онбординг, показываем онбординг
  if (user.onboarding === false) {
    return (
      <Routes>
        <Route path="*" element={<OnboardingCarousel />} />
      </Routes>
    );
  }

  // Если нужно показать ежедневную награду, показываем её
  if (showDailyReward) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <DailyRewardPage
              onComplete={onRewardComplete}
              balanceRef={balanceRef}
            />
          }
        />
      </Routes>
    );
  }

  // Основные маршруты приложения
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/lucky-spin" />} />

      {/* Страницы с общей панелью баланса и нижним меню (AppLayout) */}
      <Route element={<AppLayout />}>
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/lucky-spin" element={<LuckySpinPage />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/friends" element={<FriendsLeaderboardPage />} />
        <Route path="/shop" element={<TonStorePage />} />
        <Route path="/ton-withdrawal" element={<TonWithdrawalPage />} />
        {/* Missions & Lair (entry: "Play" on the Inventory page) */}
        <Route path="/lair" element={<LairPage />} />
      </Route>

      {/* Battle Pass — только нижнее меню, без панели баланса */}
      <Route element={<AppLayout showBalance={false} />}>
        <Route path="/battle-pass" element={<BattlePassPage />} />
      </Route>

      {/* Мини-игры: полноэкранные (без панели баланса и нижнего меню) */}
      {/* Мини-игры: на весь экран, отступ сверху под кнопки Telegram (GameShell) */}
      <Route element={<GameShell />}>
        <Route path="/arena" element={<ArenaPage />} />
        <Route path="/durak" element={<DurakPage />} />
        <Route path="/lair/brawl/:runId" element={<BrawlerPage />} />
        <Route path="/lair/match3/:runId" element={<Match3Page />} />
        <Route path="/lair/solitaire/:runId" element={<SolitairePage />} />
      </Route>

      {/* Страницы вне общего каркаса (свой layout / модальный поток) */}
      <Route
        path="/daily-reward"
        element={
          <DailyRewardPage
            onComplete={onRewardComplete}
            balanceRef={balanceRef}
          />
        }
      />
      <Route path="/collector" element={<TicketCollectorModal />} />
      <Route path="/early-access" element={<EarlyAccessPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};
